import React, { useState, useEffect } from "react";
import { X, Sparkles, User, GraduationCap, School, BookOpen, MessageSquare, Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "react-toastify";
import { collection, doc, updateDoc, Timestamp, setDoc } from "firebase/firestore";
import { db, auth } from "../../../config/firebaseConfig";
import envConfig from "../../../config/envConfig";
import { AI_LAB_COURSES, GRADE_OPTIONS } from "../constants/ai-lab-constants";
import { GAEventTrack } from "../../../utils/hooks/GA_events";
import { getFormattedBookingSource } from "../../../utils/trafficTracker";

const BookDemoModal = ({ isOpen, onClose, existingChildren = [], parentsDetails, onSuccess }) => {
  const [selectedChildMode, setSelectedChildMode] = useState("new"); // "new" or student.id
  const [studentName, setStudentName] = useState("");
  const [selectedGrade, setSelectedGrade] = useState("");
  const [selectedCourseId, setSelectedCourseId] = useState("ai-robotics");
  const [schoolName, setSchoolName] = useState("");
  const [remarks, setRemarks] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [isSuccess, setIsSuccess] = useState(false);

  const hasExistingChildren = Array.isArray(existingChildren) && existingChildren.length > 0;

  // Pre-fill fields if existing child is chosen
  useEffect(() => {
    if (hasExistingChildren) {
      // Default to first existing child if available
      const firstChild = existingChildren[0];
      setSelectedChildMode(firstChild.id);
      setStudentName(firstChild.name || "");
      setSelectedGrade(firstChild.grade || "");
      setSchoolName(firstChild.school || "");
    } else {
      setSelectedChildMode("new");
      if (parentsDetails?.accountType === "student" || parentsDetails?.accountType === "child") {
        if (parentsDetails?.name) setStudentName(parentsDetails.name);
        if (parentsDetails?.grade) setSelectedGrade(parentsDetails.grade);
      }
    }
  }, [existingChildren, hasExistingChildren, parentsDetails]);

  const handleChildSelectChange = (val) => {
    setSelectedChildMode(val);
    if (val === "new") {
      setStudentName("");
      setSelectedGrade("");
      setSchoolName("");
    } else {
      const child = existingChildren.find((c) => c.id === val);
      if (child) {
        setStudentName(child.name || "");
        setSelectedGrade(child.grade || "");
        setSchoolName(child.school || "");
      }
    }
  };

  if (!isOpen) return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    const cleanName = studentName.trim();
    if (!cleanName) {
      setError("Please enter the student's name.");
      return;
    }
    if (!selectedGrade) {
      setError("Please select the student's class/grade.");
      return;
    }

    const selectedCourseObj = AI_LAB_COURSES.find((c) => c.id === selectedCourseId) || {
      id: "ai-robotics",
      label: "AI & Robotics",
    };

    setLoading(true);

    try {
      const currentUser = auth.currentUser;
      if (!currentUser) {
        throw new Error("You must be logged in to book a free demo.");
      }

      const parentId = currentUser.uid || parentsDetails?.id || parentsDetails?.uid;

      // 1. Save or Update Child in Firestore 'students' collection if adding new child (best-effort client write)
      try {
        if (selectedChildMode === "new") {
          const newChildRef = doc(collection(db, "students"));
          await setDoc(newChildRef, {
            name: cleanName,
            grade: selectedGrade,
            school: schoolName.trim(),
            parentUid: parentId,
            createdAt: Timestamp.now(),
          });
        } else {
          const childRef = doc(db, "students", selectedChildMode);
          await updateDoc(childRef, {
            grade: selectedGrade,
            school: schoolName.trim(),
          });
        }
      } catch (err) {
        console.warn("Client-side student doc update skipped due to security rules:", err.message);
      }

      // 2. Obtain Firebase Auth Bearer ID Token
      const idToken = await currentUser.getIdToken(true);

      // 3. Call requestLiveDemoClass API (Firebase Function)
      const remarksContent = [
        `Student: ${cleanName}`,
        `Class: ${selectedGrade}`,
        schoolName.trim() ? `School: ${schoolName.trim()}` : "",
        remarks.trim() ? `Remarks: ${remarks.trim()}` : "",
      ]
        .filter(Boolean)
        .join(", ");

      const response = await fetch(`${envConfig.backendUrl}/requestLiveDemoClassV1`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${idToken}`,
        },
        body: JSON.stringify({
          courses: [
            {
              id: selectedCourseObj.id,
              name: selectedCourseObj.label,
            },
          ],
          remarks: remarksContent,
          source: getFormattedBookingSource(),
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok || data.success === false) {
        throw new Error(data.message || data.error || "Failed to submit demo request.");
      }

      setIsSuccess(true);
      toast.success("Free Demo Class booked successfully! Our team will get in touch shortly.");

      // Fire GA4 conversion event
      GAEventTrack({
        event: "ai-lab_demo_form_submit",
        data: {
          class: selectedGrade,
          subject: selectedCourseObj.label || selectedCourseObj.name || "AI & Robotics",
          page_location: window.location.href,
          source: "dashboard_modal",
        },
      });

      setTimeout(() => {
        onSuccess();
      }, 2000);
    } catch (err) {
      console.error("Error booking demo class:", err);
      const errMsg = err.message || "Something went wrong. Please try again.";
      setError(errMsg);
      toast.error(errMsg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-xs p-4 animate-fade-in">
      <div className="bg-white rounded-3xl max-w-lg w-full p-6 sm:p-8 shadow-2xl relative border border-slate-100 space-y-6 text-left max-h-[90vh] overflow-y-auto">
        {/* Close Button */}
        <button
          onClick={onClose}
          type="button"
          className="absolute top-5 right-5 text-slate-400 hover:text-slate-600 bg-slate-100 hover:bg-slate-200 p-2 rounded-full transition cursor-pointer"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Modal Header */}
        <div className="space-y-2">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-blue-50 border border-blue-100 text-blue-600 rounded-full text-xs font-black uppercase tracking-wider">
            <Sparkles className="w-3.5 h-3.5 text-amber-500 animate-pulse" />
            <span>Book Free Live Demo</span>
          </div>
          <h3 className="text-xl sm:text-2xl font-black text-slate-900 tracking-tight">
            Reserve a Free Demo Class 🚀
          </h3>
          <p className="text-xs sm:text-sm text-slate-500 font-medium">
            Fill in the details below. Our academic counsellor will confirm your live demo slot within 24 hours.
          </p>
        </div>

        {isSuccess ? (
          <div className="py-8 text-center space-y-4">
            <CheckCircle2 className="w-16 h-16 text-emerald-500 mx-auto animate-bounce" />
            <h4 className="text-xl font-extrabold text-slate-800">Demo Request Submitted!</h4>
            <p className="text-xs sm:text-sm text-slate-600 font-medium">
              We have received your demo class request for <strong className="text-blue-600">{studentName}</strong>.
              We'll send the schedule confirmation on WhatsApp.
            </p>
          </div>
        ) : (
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Child Selector ONLY if parent has existing children */}
            {hasExistingChildren && (
              <div className="space-y-1.5">
                <label className="block text-xs font-bold text-slate-700">
                  Select Student <span className="text-red-500">*</span>
                </label>
                <select
                  value={selectedChildMode}
                  onChange={(e) => handleChildSelectChange(e.target.value)}
                  className="w-full text-xs sm:text-sm border border-slate-300 rounded-xl px-4 py-2.5 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-100 bg-white font-medium"
                >
                  {existingChildren.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name} {c.grade ? `(${c.grade})` : ""}
                    </option>
                  ))}
                  <option value="new">+ Add a New Child</option>
                </select>
              </div>
            )}

            {/* Student Name */}
            <div className="space-y-1.5">
              <label className="block text-xs font-bold text-slate-700">
                Student Name <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <User className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
                <input
                  type="text"
                  placeholder="e.g. Rahul Sharma"
                  value={studentName}
                  onChange={(e) => setStudentName(e.target.value)}
                  className="w-full text-xs sm:text-sm border border-slate-300 rounded-xl pl-10 pr-4 py-2.5 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-100 bg-white font-medium"
                  required
                />
              </div>
            </div>

            {/* Class / Grade Selection */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <label className="block text-xs font-bold text-slate-700">
                  Current Class/Grade <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <GraduationCap className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
                  <select
                    value={selectedGrade}
                    onChange={(e) => setSelectedGrade(e.target.value)}
                    className="w-full text-xs sm:text-sm border border-slate-300 rounded-xl pl-10 pr-4 py-2.5 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-100 bg-white font-medium"
                    required
                  >
                    <option value="">Select Class</option>
                    {GRADE_OPTIONS.map((g) => (
                      <option key={g.value} value={g.value}>
                        {g.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Course Interested In */}
              <div className="space-y-1.5">
                <label className="block text-xs font-bold text-slate-700">
                  Course Interested In <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <BookOpen className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
                  <select
                    value={selectedCourseId}
                    onChange={(e) => setSelectedCourseId(e.target.value)}
                    className="w-full text-xs sm:text-sm border border-slate-300 rounded-xl pl-10 pr-4 py-2.5 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-100 bg-white font-medium"
                  >
                    {AI_LAB_COURSES.map((c) => (
                      <option key={c.id} value={c.id}>
                        {c.icon} {c.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* School Name (Optional) */}
            <div className="space-y-1.5">
              <label className="block text-xs font-bold text-slate-700">
                School Name <span className="text-slate-400 font-normal">(Optional)</span>
              </label>
              <div className="relative">
                <School className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
                <input
                  type="text"
                  placeholder="e.g. St. Xavier School"
                  value={schoolName}
                  onChange={(e) => setSchoolName(e.target.value)}
                  className="w-full text-xs sm:text-sm border border-slate-300 rounded-xl pl-10 pr-4 py-2.5 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-100 bg-white font-medium"
                />
              </div>
            </div>

            {/* Remarks / Notes (Optional) */}
            <div className="space-y-1.5">
              <label className="block text-xs font-bold text-slate-700">
                Any specific requests or remarks? <span className="text-slate-400 font-normal">(Optional)</span>
              </label>
              <div className="relative">
                <MessageSquare className="w-4 h-4 text-slate-400 absolute left-3.5 top-3" />
                <textarea
                  placeholder="e.g. Weekend preference, beginner in coding..."
                  value={remarks}
                  onChange={(e) => setRemarks(e.target.value)}
                  className="w-full text-xs sm:text-sm border border-slate-300 rounded-xl pl-10 pr-4 py-2.5 outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-100 bg-white font-medium min-h-[70px]"
                />
              </div>
            </div>

            {error && (
              <div className="p-3 bg-red-50 border border-red-200 text-red-600 rounded-xl text-xs font-semibold">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full py-3 bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white rounded-xl font-bold text-sm shadow-md transition duration-200 flex items-center justify-center gap-2 cursor-pointer disabled:opacity-60"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  <span>Submitting Request...</span>
                </>
              ) : (
                <>
                  <Sparkles className="w-4 h-4" />
                  <span>Confirm & Book Free Demo</span>
                </>
              )}
            </button>
          </form>
        )}
      </div>
    </div>
  );
};

export default BookDemoModal;
